import { z } from 'zod'

import { createApi, TCreateRequestOptions, xsrfTokenInterceptor } from '@fe-toolkit/axios-api-toolkit'

import axios, { AxiosRequestConfig, AxiosResponseHeaders, AxiosResponseTransformer } from 'axios'

import { ctcBaggageInterceptor } from '@fe-toolkit/utility/client/tracking-context'

import { nccProductsByUPRNResponseSchema } from '@giffgaff/broadband-common'

import { EHttpMethods, PublicApiEndpointURLs } from '@/constants'

import { createAxiosInstance } from '../utils/createApiClient'
import { memberSchema } from '../schemas/member.schemas'
import {
  broadbandPlanBaseSchema,
  cpeOrderRequestSchema,
  cpeOrderResponseSchema,
  ecommerceOrderScheduleEntrySchema,
  changePlanPreviewSchema,
  evidenceSessionEvidenceSchema,
  getEvidenceSessionResponseSchema,
  UpdateOrderActiveAppointmentsRequestSchema,
  UpdateOrderActiveAppointmentsResponseSchema,
} from '../schemas'
import { amendmentProductsSchema } from '../schemas/orders.schemas'
import { orderDetailSchema } from '../schemas/orderDetail.schemas'
import { profileOverviewSchema } from '../schemas/profile.schemas'
import { planAmendmentDetailsSchema } from '../schemas/plan-amendment-details.schemas'
import { planBuyoutClaimSchema } from '../schemas/plan-buyout-claim.schemas'
import { planBuyoutDetailsSchema } from '../schemas/plan-buyout-details.schemas'
import {
  GetOrchestratedPaymentsRequestSchema,
  GetOrchestratedPaymentsResponseSchema,
  GetPaymentDetailRequestSchema,
  GetPaymentDetailResponseSchema,
  PaymentCardResponseSchema,
} from '../schemas/payments.schemas'
import { OrchestratedPayment } from '../api.types'

const publicApi = ({ baseURL }: { baseURL: string }, options?: TCreateRequestOptions) => {
  const axiosInstance = createAxiosInstance({
    baseURL,
    withCredentials: true,
  })

  axiosInstance.interceptors.request.use(config => xsrfTokenInterceptor({ config }))
  axiosInstance.interceptors.request.use(ctcBaggageInterceptor)

  const api = createApi(
    axiosInstance,
    createEndpoint => ({
      /**
       * Member
       */
      getMember: createEndpoint({
        url: PublicApiEndpointURLs.ME,
        method: EHttpMethods.GET,
        responseSchema: memberSchema,
        validateResponse: false,
      }),

      /**
       * Orders & Plans
       */
      getProfileOverview: createEndpoint({
        url: PublicApiEndpointURLs.MEMBER_PROFILE_OVERVIEW,
        method: EHttpMethods.GET,
        requestSchema: z.object({
          memberId: z.string().optional(),
        }),
        responseSchema: profileOverviewSchema,
        transformRequest: ({ memberId }) => ({
          urlParams: {
            memberId: memberId ?? '',
          },
        }),
      }),
      // TODO: update the schema when the API is ready
      getOrderDetails: createEndpoint({
        url: PublicApiEndpointURLs.MEMBER_ORDER_DETAILS,
        method: EHttpMethods.GET,
        requestSchema: z.object({
          orderId: z.string().optional(),
        }),
        responseSchema: orderDetailSchema,
        transformRequest: data => ({
          urlParams: {
            orderId: data.orderId ?? '',
          },
        }),
      }),
      // appointments
      updateOrderActiveAppointments: createEndpoint(
        {
          url: PublicApiEndpointURLs.GET_ACTIVE_APPOINTMENTS,
          method: EHttpMethods.PUT,
          requestSchema: UpdateOrderActiveAppointmentsRequestSchema.strip(),
          responseSchema: UpdateOrderActiveAppointmentsResponseSchema,
          transformRequest: ({ orderId, appointments }) => ({
            urlParams: {
              orderId,
            },
            body: appointments,
          }),
        },
        {
          timeout: 30000,
        },
      ),
      getPlanById: createEndpoint({
        url: PublicApiEndpointURLs.PLAN_BY_ID,
        method: EHttpMethods.GET,
        requestSchema: z
          .object({
            planId: z.string().optional(),
          })
          .strip(),
        responseSchema: broadbandPlanBaseSchema.strip(),
        transformRequest: ({ planId }) => ({
          urlParams: {
            planId: planId ?? '',
          },
        }),
      }),
      getPlanAmendmentDetails: createEndpoint({
        url: PublicApiEndpointURLs.PLAN_AMENDMENT_DETAILS_BY_PLAN_ID,
        method: EHttpMethods.GET,
        requestSchema: z
          .object({
            planId: z.string().optional(),
          })
          .strip(),
        responseSchema: planAmendmentDetailsSchema,
        transformRequest: ({ planId }) => ({
          urlParams: {
            planId: planId ?? '',
          },
        }),
      }),
      getPlanBuyoutDetails: createEndpoint({
        url: PublicApiEndpointURLs.PLAN_BUYOUT_DETAILS_BY_PLAN_ID,
        method: EHttpMethods.GET,
        requestSchema: z
          .object({
            planId: z.string().optional(),
          })
          .strip(),
        responseSchema: planBuyoutDetailsSchema.strip(),
        transformRequest: ({ planId }) => ({
          urlParams: {
            planId: planId ?? '',
          },
        }),
      }),
      getEvidenceSession: createEndpoint({
        url: PublicApiEndpointURLs.EVIDENCE_SESSION_BY_ID,
        method: EHttpMethods.GET,
        requestSchema: z
          .object({
            evidenceSessionId: z.string().optional(),
          })
          .strip(),
        responseSchema: getEvidenceSessionResponseSchema,
        transformRequest: ({ evidenceSessionId }) => ({
          urlParams: {
            evidenceSessionId: evidenceSessionId ?? '',
          },
        }),
      }),
      deleteEvidenceSessionEvidence: createEndpoint({
        url: PublicApiEndpointURLs.EVIDENCE_SESSION_EVIDENCE_BY_ID,
        method: EHttpMethods.DELETE,
        requestSchema: z
          .object({
            evidenceSessionId: z.string().optional(),
            evidenceId: z.string().optional(),
          })
          .strip(),
        responseSchema: z.unknown(),
        transformRequest: ({ evidenceSessionId, evidenceId }) => ({
          urlParams: {
            evidenceSessionId: evidenceSessionId ?? '',
            evidenceId: evidenceId ?? '',
          },
        }),
      }),
      updateEvidenceSession: createEndpoint({
        url: PublicApiEndpointURLs.EVIDENCE_SESSION_BY_ID,
        method: EHttpMethods.PATCH,
        requestSchema: z
          .object({
            evidenceSessionId: z.string(),
            claimAmountInPence: z.number(),
          })
          .strip(),
        responseSchema: z.unknown(),
        transformRequest: ({ evidenceSessionId, claimAmountInPence }) => ({
          urlParams: {
            evidenceSessionId,
          },
          body: { attributes: { claimAmountInPence } },
        }),
      }),
      confirmEvidenceSession: createEndpoint({
        url: PublicApiEndpointURLs.EVIDENCE_SESSION_CONFIRMATION,
        method: EHttpMethods.POST,
        requestSchema: z
          .object({
            evidenceSessionId: z.string(),
          })
          .strip(),
        responseSchema: z.unknown(),
        transformRequest: ({ evidenceSessionId }) => ({
          urlParams: {
            evidenceSessionId,
          },
        }),
      }),
      createPlanBuyoutClaim: createEndpoint({
        url: PublicApiEndpointURLs.PLAN_BUYOUT_CLAIM_BY_PLAN_ID,
        method: EHttpMethods.POST,
        requestSchema: z
          .object({
            planId: z.string().optional(),
          })
          .strip(),
        responseSchema: planBuyoutClaimSchema.strip(),
        transformRequest: ({ planId }) => ({
          urlParams: {
            planId: planId ?? '',
          },
        }),
      }),
      // get products by uprn
      getProductsByUprn: createEndpoint({
        url: PublicApiEndpointURLs.PRODUCTS_BY_UPRN,
        method: EHttpMethods.GET,
        responseSchema: nccProductsByUPRNResponseSchema,
        requestSchema: z
          .object({
            journey: z.string().optional(),
            memberId: z.string().optional(),
            uprn: z.string().optional(),
            vendorApiVersion: z.string().optional(),
            productId: z.string().optional(),
          })
          .strip(),
      }),
      getCpeOrder: createEndpoint({
        url: PublicApiEndpointURLs.CPE_ORDERS,
        method: EHttpMethods.GET,
        requestSchema: cpeOrderRequestSchema,
        responseSchema: cpeOrderResponseSchema,
      }),
      getECommerceOrderSchedule: createEndpoint({
        url: PublicApiEndpointURLs.ORDER_SCHEDULES,
        method: EHttpMethods.GET,
        requestSchema: z.object({
          scheduleId: z.string().optional(),
        }),
        responseSchema: ecommerceOrderScheduleEntrySchema,
        transformRequest: ({ scheduleId }) => ({
          urlParams: {
            scheduleId: scheduleId ?? '',
          },
        }),
      }),
      confirmChangePlan: createEndpoint({
        url: PublicApiEndpointURLs.CONFIRM_PLAN_CHANGE_BY_PLAN_ID,
        method: EHttpMethods.POST,
        requestSchema: z.object({
          planId: z.string().optional(),
          newPlanId: z.string().optional(),
        }),
        responseSchema: z.object({
          newPlanId: z.string(),
          newOrderId: z.string(),
          newOrderKey: z.string(),
          paymentSessionUrl: z.string().optional(),
        }),
        transformRequest: ({ planId }) => ({
          urlParams: {
            planId: planId ?? '',
          },
        }),
      }),
      getChangePlanPreview: createEndpoint({
        url: PublicApiEndpointURLs.PLAN_CHANGE_PREVIEW_BY_PLAN_ID,
        method: EHttpMethods.GET,
        responseSchema: changePlanPreviewSchema.strip(),
        requestSchema: z.object({
          productId: z.string(),
          planId: z.string().optional(),
        }),
        transformRequest: ({ planId }) => ({
          urlParams: {
            planId: planId ?? '',
          },
        }),
      }),
      getOrchestratedPayments: createEndpoint(
        {
          url: PublicApiEndpointURLs.GET_ORCHESTRATED_PAYMENTS,
          method: EHttpMethods.GET,
          requestSchema: GetOrchestratedPaymentsRequestSchema,
          responseSchema: GetOrchestratedPaymentsResponseSchema,
        },
        {
          paramsSerializer: { indexes: null },
          transformResponse: (axios.defaults.transformResponse as AxiosResponseTransformer[]).concat(
            (data: OrchestratedPayment[], headers: AxiosResponseHeaders) => {
              // Convert headers to a numeric value or default to 1
              const numericHeader = (name: string) => +(headers.get(name) ?? 1)

              return {
                data,
                // Allow pagination by converting the relevant headers into a pageMeta object
                // including pagination details
                pageMeta: {
                  totalCount: numericHeader('X-Total-Count'),
                  totalPages: numericHeader('X-Total-Pages'),
                  pageSize: numericHeader('X-Page-Size'),
                  currentPage: numericHeader('X-Current-Page'),
                },
              }
            },
          ),
        },
      ),
      getPaymentDetailsByCallerReferenceId: createEndpoint({
        url: PublicApiEndpointURLs.GET_PAYMENTS_BY_CALLER_REFERENCE_ID,
        method: EHttpMethods.GET,
        requestSchema: GetPaymentDetailRequestSchema,
        responseSchema: GetPaymentDetailResponseSchema,
      }),
      getMemberCardDetails: createEndpoint({
        url: PublicApiEndpointURLs.GET_MEMBER_CARD_DETAILS,
        method: EHttpMethods.GET,
        responseSchema: PaymentCardResponseSchema,
      }),
      getPlanAmendmentProducts: createEndpoint({
        url: PublicApiEndpointURLs.GET_PLAN_AMENDMENT_PRODUCTS,
        method: EHttpMethods.GET,
        requestSchema: z
          .object({
            planId: z.string(),
            nccTransactionId: z.string(),
          })
          .strip(),
        responseSchema: amendmentProductsSchema.strip(),
        transformRequest: ({ planId }) => ({
          urlParams: {
            planId,
          },
        }),
      }),
      createDraftPlan: createEndpoint({
        url: PublicApiEndpointURLs.CREATE_DRAFT_PLAN,
        method: EHttpMethods.POST,
        requestSchema: z
          .object({
            nccTransactionId: z.string(),
            productId: z.string(),
            existingPlanId: z.string(),
          })
          .strip(),
        responseSchema: broadbandPlanBaseSchema.strip(),
      }),
    }),
    options,
  )

  // Bypasses createEndpoint: its transformRequest.body handling does `{ ...body }`, which strips
  // FormData down to a plain object and silently drops the file, so this posts directly instead.
  const uploadEvidenceSessionEvidence = async (
    { evidenceSessionId, file }: { evidenceSessionId: string; file: File },
    axiosRequestConfig?: AxiosRequestConfig,
  ) => {
    const formData = new FormData()

    formData.append('file', file)

    const response = await axiosInstance.post(
      PublicApiEndpointURLs.EVIDENCE_SESSION_EVIDENCE.replace(':evidenceSessionId', evidenceSessionId),
      formData,
      axiosRequestConfig,
    )

    return { data: evidenceSessionEvidenceSchema.parse(response.data) }
  }

  return {
    api,
    axiosInstance,
    uploadEvidenceSessionEvidence,
  }
}

export default publicApi
